import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHorizRounded";
import MoveUpRoundedIcon from "@mui/icons-material/MoveUpRounded";
import StarBorderIcon from "@mui/icons-material/StarBorderRounded";
import {
  Badge,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext.ts";
import type { ReportGroup } from "../../../../../../api/types/svTypes.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import ReportingProductIcon from "../../../../../common/domain-icons/ReportingProductIcon.tsx";
import GroupActions from "../../groups/actions/GroupActions.tsx";
import type { GroupType } from "../../groups/GroupType.ts";
import GroupNamesMultilineEllipsis from "./GroupNamesMultilineEllipsis.tsx";
import ReorderGroupsPopover from "./ReorderGroupsPopover.tsx";

const groupActionSlotSx = {
  width: 36,
  minHeight: 24,
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
};

type SideMenuFilter = "all" | "favorites" | string;

interface Props {
  title: string;
  selectedFilter: SideMenuFilter;
  onFilterChange: (filter: SideMenuFilter) => void;
  groups: ReportGroup[];
  reportCountsByGroup: Record<string, number>;
  groupType: GroupType;
  onAddGroup: () => void;
  onReorderGroups: (groups: ReportGroup[]) => Promise<void>;
  allItemsLabel?: string;
  groupsLabel?: string;
}

const ReportsSideMenu = ({
  title,
  selectedFilter,
  onFilterChange,
  groups,
  reportCountsByGroup,
  groupType,
  onAddGroup,
  onReorderGroups,
  allItemsLabel,
  groupsLabel,
}: Props) => {
  const { clientCode } = useClientContext();
  const { hasAnyPermissionForClient } = useClientPermissionsContext();
  const [reorderAnchorEl, setReorderAnchorEl] = useState<HTMLElement | null>(null);
  const [actionMenuRef, setActionMenuRef] = useState<HTMLElement | null>(null);
  const [currentGroupId, setCurrentGroupId] = useState<string>();

  const canManageGroups = hasAnyPermissionForClient(clientCode, ["ManageAllReports"]);

  const onShowGroupMenu = useCallback((e: React.MouseEvent<HTMLElement>, groupId: string) => {
    e.stopPropagation();
    setCurrentGroupId(groupId);
    setActionMenuRef(e.currentTarget);
  }, []);

  return (
    <Stack
      sx={[
        {
          gap: 2.5,
        },
        (theme) => ({
          width: 300,
          minWidth: 300,
          borderRight: `1px solid ${theme.palette.divider}`,
          height: "100%",
          overflow: "hidden",
          p: 2,
        }),
      ]}
    >
      <List
        subheader={
          <ListSubheader
            component="div"
            disableSticky
            sx={{ lineHeight: "32px", bgcolor: "transparent", px: 2, color: "text.primary", fontSize: "15px" }}
          >
            {title}
          </ListSubheader>
        }
        disablePadding
      >
        <ListItemButton selected={selectedFilter === "all"} onClick={() => onFilterChange("all")} sx={{ py: 0.5 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ReportingProductIcon color={selectedFilter === "all" ? "primary" : "secondary"} />
          </ListItemIcon>
          <ListItemText
            primary={allItemsLabel ?? "All Reports"}
            slotProps={{ primary: { color: selectedFilter === "all" ? "primary" : "textPrimary" } }}
          />
        </ListItemButton>
        <ListItemButton
          selected={selectedFilter === "favorites"}
          onClick={() => onFilterChange("favorites")}
          sx={{ py: 0.5 }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <StarBorderIcon color={selectedFilter === "favorites" ? "primary" : "secondary"} />
          </ListItemIcon>
          <ListItemText
            primary="My Favorites"
            slotProps={{ primary: { color: selectedFilter === "favorites" ? "primary" : "textPrimary" } }}
          />
        </ListItemButton>
      </List>
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <ListSubheader
          component="div"
          disableSticky
          sx={{ lineHeight: "32px", display: "flex", alignItems: "center", bgcolor: "transparent", pr: 0 }}
        >
          <ListItemText
            primary={groupsLabel ?? "Reports by Group"}
            slotProps={{ primary: { color: "textPrimary", variant: "subtitle1" } }}
          />
          {canManageGroups && (
            <Stack direction="row" sx={groupActionSlotSx}>
              <Tooltip title="Reorder Groups" placement="bottom" arrow>
                <IconButton onClick={(e) => setReorderAnchorEl(e.currentTarget)}>
                  <MoveUpRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
          {canManageGroups && (
            <Stack direction="row" sx={groupActionSlotSx}>
              <IconButton onClick={onAddGroup} color="primary">
                <AddRoundedIcon />
              </IconButton>
            </Stack>
          )}
        </ListSubheader>
        <List disablePadding sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {groups.map((group) => (
            <ListItemButton
              key={group.id}
              selected={selectedFilter === group.id}
              onClick={() => onFilterChange(group.id)}
              sx={{
                py: 0.5,
                pr: 0,
                ...(canManageGroups && {
                  "& .group-actions-button": { display: "none" },
                  "&:hover": {
                    "& .group-actions-button": { display: "inline-flex" },
                    "& .group-badge": { display: "none" },
                  },
                }),
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FolderOpenRoundedIcon color={selectedFilter === group.id ? "primary" : "secondary"} />
              </ListItemIcon>
              <ListItemText
                primary={
                  <GroupNamesMultilineEllipsis
                    maxLines={2}
                    typographyProps={{ variant: "body1", component: "span" }}
                    tooltipProps={{
                      title: group.caption,
                      enterDelay: 500,
                      enterNextDelay: 500,
                      disableInteractive: true,
                    }}
                    tooltipPlacement="bottom"
                  >
                    {group.caption}
                  </GroupNamesMultilineEllipsis>
                }
                slotProps={{
                  primary: { color: selectedFilter === group.id ? "primary" : "textPrimary", variant: "body1" },
                }}
              />
              <Stack direction="row" sx={groupActionSlotSx}>
                <Badge
                  className="group-badge"
                  badgeContent={reportCountsByGroup[group.id] ?? 0}
                  sx={{ color: "text.secondary" }}
                />
                {canManageGroups && (
                  <IconButton
                    className="group-actions-button"
                    onClick={(e) => onShowGroupMenu(e, group.id)}
                    sx={{ p: 0.25 }}
                  >
                    <MoreHorizIcon />
                  </IconButton>
                )}
              </Stack>
            </ListItemButton>
          ))}
        </List>
      </Stack>
      {actionMenuRef && currentGroupId && (
        <GroupActions
          anchorEl={actionMenuRef}
          currentGroupId={currentGroupId}
          showRename
          showDelete
          type={groupType}
          onClose={() => setActionMenuRef(null)}
        />
      )}
      {reorderAnchorEl && (
        <ReorderGroupsPopover
          anchorEl={reorderAnchorEl}
          onClose={() => setReorderAnchorEl(null)}
          groups={groups}
          onApply={onReorderGroups}
        />
      )}
    </Stack>
  );
};

export default ReportsSideMenu;
export type { SideMenuFilter };
